import { Checkbox, Col, DatePicker, Form, Input, Row, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { useSelector } from "react-redux";
import LabelWithTooltip from "../../../../../common/components/form/labels/LabelWithTooltip";
import { rowGutter } from "../../../../../common/constants";
import {
  datePickerFormItemProps,
  datePickerStandardProps,
  disableDatePickerPresentAndFuture,
  fillBirthDateFromPin,
  ibanNormalizeFunction,
  phoneNumberNormalizeFunction,
  quillEditorStandardProps,
  quillFormItemProps,
  selectStandardProps,
  upperCaseStringNormalizeFunction
} from "../../../../../common/utils/formUtils";
import { regexPatterns, validations } from "../../../../../common/utils/validationUtils";
import { selectIsCurrentAgentTypePlus } from "../../../../auth/ducks";
import AcademicDegreeAfterSelect from "../../../../enumerations/components/form/AcademicDegreeAfterSelect";
import AcademicDegreeSelect from "../../../../enumerations/components/form/AcademicDegreeSelect";
import DistrictOfficeAutoComplete from "../../../../enumerations/components/form/DistrictOfficeAutoComplete";
import { AgentSecondaryStatus, AgentStatus, FinancialSector } from "../../../enums";
import { CreateUpdateSelfEmployedAgent } from "../../../types";
import { isLoanSpecialistDisabled, isRealtySpecialistDisabled } from "../../../utils";

interface Props {
  searchInProgress?: boolean;
  onStatusChange: (value: AgentStatus) => void;
  onSectorChange: (value: FinancialSector[]) => void;
  onSecondaryStatusChange: (value: AgentSecondaryStatus) => void;
  onCrnSearchOrChange?: (crn: string) => void;
}

const colSpan = 4;

export const SelfEmployedAgentDataFormPart = ({
  searchInProgress,
  onStatusChange,
  onSectorChange,
  onCrnSearchOrChange,
  onSecondaryStatusChange
}: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useSelector(selectIsCurrentAgentTypePlus);

  const form = Form.useFormInstance<CreateUpdateSelfEmployedAgent>();
  const sectors = Form.useWatch("sectors", form);

  const status = Form.useWatch("status", form);
  const statusStartDate = Form.useWatch("statusStartDate", form);
  const statusStartDatePlusOne = statusStartDate ? dayjs(statusStartDate).add(1, "day") : undefined;

  const secondaryStatus = Form.useWatch("secondaryStatus", form);
  const secondaryStatusStartDate = Form.useWatch("secondaryStatusStartDate", form);
  const secondaryStatusStartDatePlusOne = secondaryStatusStartDate
    ? dayjs(secondaryStatusStartDate).add(1, "day")
    : undefined;

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="companyRegistrationNumber"
            label={t("agent.attrs.companyRegistrationNumber")}
            rules={[validations.notBlank, validations.crn]}
          >
            {onCrnSearchOrChange ? (
              <Input.Search
                loading={searchInProgress}
                onSearch={onCrnSearchOrChange}
                onChange={e => onCrnSearchOrChange?.(e.target.value)}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="companyName"
            label={t("agent.attrs.companyName")}
            rules={[validations.notBlank, validations.size(1, 255)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <AcademicDegreeSelect
            formItemProps={{ name: "academicDegree", label: t("agent.attrs.academicDegree") }}
            selectProps={{ allowClear: true }}
          />
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="firstName"
            label={t("agent.attrs.firstName")}
            rules={[validations.notBlank, validations.size(1, 255), validations.pattern(regexPatterns.wordRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="lastName"
            label={t("agent.attrs.lastName")}
            rules={[
              validations.notBlank,
              validations.size(1, 255),
              validations.pattern(regexPatterns.wordRegex),
              validations.pattern(regexPatterns.agentSortNameRegex)
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <AcademicDegreeAfterSelect
            formItemProps={{ name: "academicDegreeAfter", label: t("agent.attrs.academicDegreeAfter") }}
            selectProps={{ allowClear: true }}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item name="taxId" label={t("agent.attrs.taxId")} rules={[validations.length(10), validations.numeric]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <DistrictOfficeAutoComplete
            formItemProps={{
              name: "districtOffice",
              label: t("agent.attrs.districtOffice"),
              rules: [validations.size(1, 255)]
            }}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="vatId"
            label={t("agent.attrs.vatId")}
            rules={[validations.length(12), validations.pattern(regexPatterns.vatIdRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="tradeRegisterNumber"
            label={t("agent.attrs.tradeRegisterNumber")}
            rules={[validations.size(1, 255)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="personalIdentificationNumber"
            label={
              <LabelWithTooltip
                tooltip={t("helpers.pinFormat")}
                label={t("agent.attrs.personalIdentificationNumberLong")}
              />
            }
            rules={[validations.notBlank, validations.pin]}
          >
            <Input onChange={event => fillBirthDateFromPin(event.target.value, form)} />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="birthDate"
            label={t("agent.attrs.birthDate")}
            rules={[validations.notNull, validations.notPresentAndFuture]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} showNow={false} disabledDate={disableDatePickerPresentAndFuture} />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="identityCardNumber"
            label={t("agent.attrs.identityCardNumber")}
            rules={[validations.size(8, 10), validations.pattern(regexPatterns.idCardRegex)]}
            normalize={upperCaseStringNormalizeFunction}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 2}>
          <Form.Item
            name="status"
            label={isTopAgentPlus ? t("agent.helpers.agentStatusSFAPlusLabel") : t("agent.enums.agentStatus._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.values(AgentStatus).map(type => ({
                value: type,
                label: t(`agent.enums.agentStatus.${type}`)
              }))}
              onChange={onStatusChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="statusStartDate"
            label={t("agent.attrs.statusStartDate")}
            {...datePickerFormItemProps}
            rules={[
              (status === AgentStatus.TIPPER_WITH_CONTRACT || status === AgentStatus.BROKER_WITH_CONTRACT) &&
              isTopAgentPlus
                ? validations.notNull
                : validations.none
            ]}
          >
            <DatePicker {...datePickerStandardProps} allowClear={true} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="statusEndDate"
            label={t("agent.attrs.statusEndDate")}
            {...datePickerFormItemProps}
            rules={[
              statusStartDatePlusOne ? validations.notBefore(statusStartDatePlusOne) : validations.none,
              (status === AgentStatus.TIPPER_WITHOUT_CONTRACT ||
                status === AgentStatus.BROKER_WITH_TERMINATED_CONTRACT) &&
              !!statusStartDate &&
              isTopAgentPlus
                ? validations.notNull
                : validations.none
            ]}
            dependencies={["statusStartDate"]}
          >
            <DatePicker
              {...datePickerStandardProps}
              allowClear={true}
              minDate={statusStartDatePlusOne}
              disabled={status === AgentStatus.TIPPER_WITH_CONTRACT || status === AgentStatus.BROKER_WITH_CONTRACT}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="nbsRegistrationNumber"
            label={t("agent.attrs.nbsRegistrationNumber")}
            rules={[validations.size(1, 32), validations.numeric]}
          >
            <Input
              disabled={status === AgentStatus.TIPPER_WITHOUT_CONTRACT || status === AgentStatus.TIPPER_WITH_CONTRACT}
            />
          </Form.Item>
        </Col>
      </Row>

      {isTopAgentPlus ? (
        <Row gutter={rowGutter}>
          <Col span={colSpan * 2}>
            <Form.Item
              name="secondaryStatus"
              label={t("agent.enums.agentSecondaryStatus._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.values(AgentSecondaryStatus).map(type => ({
                  value: type,
                  label: t(`agent.enums.agentSecondaryStatus.${type}`)
                }))}
                onChange={onSecondaryStatusChange}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="secondaryStatusStartDate"
              label={t("agent.attrs.secondaryStatusStartDate")}
              {...datePickerFormItemProps}
              rules={[
                secondaryStatus === AgentSecondaryStatus.AGENT_WITH_CONTRACT && isTopAgentPlus
                  ? validations.notNull
                  : validations.none
              ]}
            >
              <DatePicker {...datePickerStandardProps} allowClear={true} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="secondaryStatusEndDate"
              label={t("agent.attrs.secondaryStatusEndDate")}
              {...datePickerFormItemProps}
              rules={[
                secondaryStatusStartDatePlusOne
                  ? validations.notBefore(secondaryStatusStartDatePlusOne)
                  : validations.none,
                secondaryStatus === AgentSecondaryStatus.AGENT_WITHOUT_CONTRACT &&
                !!secondaryStatusStartDate &&
                isTopAgentPlus
                  ? validations.notNull
                  : validations.none
              ]}
              dependencies={["secondaryStatusStartDate"]}
            >
              <DatePicker
                {...datePickerStandardProps}
                allowClear={true}
                disabled={secondaryStatus === AgentSecondaryStatus.AGENT_WITH_CONTRACT}
              />
            </Form.Item>
          </Col>
        </Row>
      ) : undefined}

      <Row gutter={rowGutter}>
        <Col span={colSpan * 2}>
          <Form.Item name="sectors" label={t("common.financialSectors")} rules={[validations.none]}>
            <Select
              {...selectStandardProps}
              mode="multiple"
              maxTagCount="responsive"
              options={Object.keys(FinancialSector).map(sector => ({
                value: sector,
                label: t("agent.enums.financialSector." + sector)
              }))}
              onChange={onSectorChange}
            />
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            name="realtySpecialist"
            valuePropName="checked"
            rules={[validations.none]}
            className="form-item-without-label"
            initialValue={false}
          >
            <Checkbox disabled={isRealtySpecialistDisabled(status, sectors)}>
              <LabelWithTooltip
                tooltip={t("agent.helpers.validationHintRealtySpecialist")}
                label={t("agent.attrs.realtySpecialist")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            name="loanSpecialist"
            valuePropName="checked"
            rules={[validations.none]}
            className="form-item-without-label"
            initialValue={false}
          >
            <Checkbox disabled={isLoanSpecialistDisabled(status, sectors)}>
              <LabelWithTooltip
                tooltip={t("agent.helpers.validationHintLoanSpecialist")}
                label={t("agent.attrs.loanSpecialist")}
              />
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="email"
            label={t("agent.attrs.email")}
            rules={[validations.notBlank, validations.size(1, 254), validations.email]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="phone"
            label={t("agent.attrs.phone")}
            rules={[validations.notBlank, validations.size(1, 19), validations.mobilePhoneNumber]}
            normalize={phoneNumberNormalizeFunction}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="web"
            label={t("agent.attrs.web")}
            rules={[validations.size(1, 255), validations.pattern(regexPatterns.webPageRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 2}>
          <Form.Item
            name="bankAccountNumber"
            label={t("agent.attrs.bankAccountNumber")}
            rules={[validations.size(1, 34), validations.iban]}
            normalize={ibanNormalizeFunction}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 4}>
          <Form.Item
            name="note"
            label={t("agent.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
